import { AnimatePresence, motion } from "framer-motion";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import ContentModal from "../components/ContentModal";
import ContentCard from "../components/dashboard/ContentCard";
import dreamWorldProvider from "../DreamWorldProvider";
import { toggleLikeContent } from "../store/dreamworldSlice";
import { AppDispatch, RootState } from "../store/store";
import {
  resetTagContent,
  selectTagContent,
  selectTagHasMore,
  setTagContent,
} from "../store/tagSlice";
import { LibraryItem } from "../types/library";
import LoadingIndicator from "../components/ui/LoadingIndicator";
import PageLayout from "../components/ui/PageLayout";

const TagPage: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const { tagName } = useParams<{ tagName: string }>();
  const [selectedContent, setSelectedContent] = useState<LibraryItem | null>(
    null
  );
  const dispatch = useDispatch<AppDispatch>();
  const userGlobalData = useSelector(
    (state: RootState) => state.dreamworld.userGlobalData
  );
  const content = useSelector(selectTagContent);
  const hasMore = useSelector(selectTagHasMore);
  const [isLoading, setIsLoading] = useState(false);
  const lastVisible = useSelector((state: RootState) => state.tag.lastVisible);
  const error = useSelector((state: RootState) =>
    state.tag.loading === "failed" ? "Failed to fetch content." : null
  );

  const loadMoreContent = useCallback(async () => {
    if (isLoading || !hasMore) return;
    if (tagName) {
      setIsLoading(true);
      const data = await dreamWorldProvider.getContentByTag(
        tagName,
        lastVisible || undefined
      );
      dispatch(setTagContent(data?.items || []));
      setIsLoading(false);
    }
  }, [hasMore, lastVisible, tagName]);

  useEffect(() => {
    dispatch(resetTagContent());
    loadMoreContent();
  }, [tagName]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
          document.documentElement.offsetHeight - 1000 &&
        hasMore &&
        !isLoading
      ) {
        loadMoreContent();
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore]);

  const handleLike = (id: string) => {
    if (userGlobalData) {
      dispatch(
        toggleLikeContent({ contentId: id, userId: userGlobalData.user.uid })
      );
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-950 via-purple-950 to-slate-900 min-h-screen text-white">
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl sm:text-4xl font-bold mb-8">
            Content tagged with{" "}
            <span className="text-purple-400">#{tagName}</span>
          </h1>

          {error && <p className="text-red-500">{error}</p>}

          <div className="columns-2 sm:columns-2 lg:columns-3 xl:columns-4 gap-0.5 sm:gap-1 lg:gap-1 space-y-3 sm:space-y-4 lg:space-y-6">
            <AnimatePresence>
              {content.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ delay: index * 0.05 }}
                  className="break-inside-avoid"
                >
                  <ContentCard
                    item={item}
                    onLike={handleLike}
                    onClick={() => setSelectedContent(item)}
                    viewMode="masonry"
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {isLoading === true && (
            <div className="flex justify-center items-center py-8">
              <LoadingIndicator />
            </div>
          )}

          {!hasMore && content.length > 0 && (
            <div className="text-center py-8 text-gray-400">
              You've reached the end!
            </div>
          )}

          {content.length === 0 && !isLoading && !error && (
            <div className="text-center py-8 text-gray-400">
              No content found for this tag.
            </div>
          )}

          <AnimatePresence>
            {selectedContent && (
              <ContentModal
                contentId={selectedContent.id}
                source="tag"
                onClose={() => setSelectedContent(null)}
                onLike={handleLike}
              />
            )}
          </AnimatePresence>
        </div>
      </PageLayout>
    </div>
  );
};

export default TagPage;
